import { useEffect, useState } from "react"
import {
  Users,
  FileText,
  DollarSign,
  BookOpen,
  Shield,
  Share2,
  Bell,
  Target,
  UserPlus,
  GitBranch,
  ClipboardList,
  Users2,
  TrendingUp,
  UserCheck,
  ShieldAlert,
  LayoutGrid,
} from "lucide-react"
import { Layout } from "@/shared/components"
import {
  ClientEnrollmentWizard,
  ClientSelfEnrollmentWizard,
  AgentSalesReports,
  TrainingDashboard,
  TrainingGate,
  AgentComplianceMaterials,
  AgentShareInstructions,
  AgentPendingApplications,
  AgentCampaignSelector,
  useAgentStore
} from "@/features/agents"
import { useCampaign, useCampaignStore } from "@/features/campaigns"
import { ReferralLinkCard } from "@/features/registration/components/ReferralLinkCard"
import { OnboardingFunnel } from "@/features/referral-analytics/components/OnboardingFunnel"
import { RecruitTrackingSection } from "@/features/sales-manager/components/RecruitTrackingSection"
import { DownlineTree } from "@/features/sales-manager/components/DownlineTree"
import { TeamSalesReports } from "@/features/sales-manager/components/TeamSalesReports"
import { TeamRecruitsSection } from "@/features/sales-manager/components/TeamRecruitsSection"
import { RecruitApprovalPanel } from "@/features/sales-manager/components/RecruitApprovalPanel"
import { AgentManagementPanel } from "@/features/sales-manager/components/AgentManagementPanel"
import { TeamOrganizationManager } from "@/features/sales-manager/components/TeamOrganizationManager"


export default function SalesManagerDashboard() {
  // Zustand stores
  const { fetchAgentData } = useAgentStore();
  const {
    selectedCampaign,
    availableCampaigns,
    setSelectedCampaign,
    fetchCampaigns,
    isLoading: campaignsLoading
  } = useCampaignStore();

  // Keep using CampaignContext for backward compatibility
  const { selectedCampaign: contextCampaign, selectCampaign: contextSelectCampaign, campaigns: contextCampaigns } = useCampaign();

  // Initialize stores on mount
  useEffect(() => {
    fetchAgentData();
    fetchCampaigns();
  }, [fetchAgentData, fetchCampaigns]);

  // #1 - Auto-select "ACA HEALTH INSURANCE" as default on login
  useEffect(() => {
    if (!selectedCampaign && availableCampaigns.length > 0) {
      const acaCampaign = availableCampaigns.find(
        (c: any) => 
          c.name?.toUpperCase().includes('ACA HEALTH INSURANCE') || 
          c.code?.toUpperCase().includes('ACA') ||
          c.name?.toUpperCase().includes('ACA')
      ) || availableCampaigns[0]
      
      if (acaCampaign) {
        setSelectedCampaign(acaCampaign)
        contextSelectCampaign(acaCampaign)
      }
    }
  }, [availableCampaigns, selectedCampaign, setSelectedCampaign, contextSelectCampaign])

  // Get initial tab from URL hash or default to enrollment (Requirement #3)
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '')
    const validTabs = [
      "enrollment", "self-enrollment", "pending-applications", "sales-reports",
      "sharing", "referrals", "training", "compliance", "campaign-selection",
      // Sales Manager specific tabs
      "own-sales", "own-recruits", "team-sales", "team-recruits",
      "recruit-tracking", "downline-tree", "recruit-approval", "agent-management", "organization-manager"
    ]
    return validTabs.includes(hash) ? hash : "enrollment"
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  const [pendingViewKey, setPendingViewKey] = useState<number>(0)
  const [resumeRequested, setResumeRequested] = useState<boolean>(false)
  const [intendedDestination, setIntendedDestination] = useState<string | null>(null)

  // Handle direct URL access - only redirect if no campaign and explicitly on tabs requiring it
  useEffect(() => {
    const currentTab = activeTab
    const requiresCampaign = ['self-enrollment', 'pending-applications'].includes(currentTab)

    if (requiresCampaign && !selectedCampaign && !campaignsLoading) {
      setIntendedDestination(currentTab)
      handleTabChange('campaign-selection')
    }
  }, [activeTab, selectedCampaign, campaignsLoading])

  // Redirect to intended destination after campaign selection
  useEffect(() => {
    if (selectedCampaign && intendedDestination) {
      handleTabChange(intendedDestination)
      setIntendedDestination(null)
    }
  }, [selectedCampaign, intendedDestination])

  // Handle URL hash changes (browser back/forward or direct URL access)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '')
      const validTabs = [
        "enrollment", "self-enrollment", "pending-applications", "sales-reports",
        "sharing", "referrals", "training", "compliance", "campaign-selection",
        "own-sales", "own-recruits", "team-sales", "team-recruits",
        "recruit-tracking", "downline-tree", "recruit-approval", "agent-management", "organization-manager"
      ]

      if (validTabs.includes(hash)) {
        setActiveTab(hash)
      }
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [selectedCampaign])


  // Listen for pending updates fired from other views
  useEffect(() => {
    const onPendingUpdated = () => {
      if (activeTab === 'pending-applications') {
        setPendingViewKey(Date.now())
      }
    }
    window.addEventListener('pending:updated', onPendingUpdated as any)
    return () => window.removeEventListener('pending:updated', onPendingUpdated as any)
  }, [activeTab])

  useEffect(() => {
    const onNavigateEnrollment = () => {
      setResumeRequested(true)
      handleTabChange('enrollment')
      setMobileSidebarOpen(false)
    }

    const onNavigateEnrollmentFollowup = () => {
      setResumeRequested(true)
      handleTabChange('enrollment')
      setMobileSidebarOpen(false)
    }

    const onNavigateTraining = () => {
      handleTabChange('training')
      setMobileSidebarOpen(false)
    }

    const onResetResumeFlag = () => {
      setResumeRequested(false)
    }

    window.addEventListener('navigate:enrollment', onNavigateEnrollment as any)
    window.addEventListener('navigate:enrollment-followup', onNavigateEnrollmentFollowup as any)
    window.addEventListener('navigate:training', onNavigateTraining as any)
    window.addEventListener('reset-resume-flag', onResetResumeFlag as any)
    return () => {
      window.removeEventListener('navigate:enrollment', onNavigateEnrollment as any)
      window.removeEventListener('navigate:enrollment-followup', onNavigateEnrollmentFollowup as any)
      window.removeEventListener('navigate:training', onNavigateTraining as any)
      window.removeEventListener('reset-resume-flag', onResetResumeFlag as any)
    }
  }, [])

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    // Update URL hash without triggering page reload
    window.history.replaceState(null, '', `#${tabId}`)
  }

  // Navigation Items - REORDERED 1-9 per Specification
  const navigationItems = [
    // #1 - Client Enrollment (default home)
    { id: "enrollment", label: "Client Enrollment", icon: Users, section: "agent", hidden: false },
    // #2 - Self Enrollment (HIDDEN until testing perfected)
    { id: "self-enrollment", label: "Self Enrollment", icon: FileText, section: "agent", hidden: true },
    // #3 - Applications (placed under self-enrollment)
    { id: "pending-applications", label: "Applications", icon: Bell, section: "agent", hidden: false },
    // #4 - Sales Reports
    { id: "sales-reports", label: "Sales Reports", icon: DollarSign, section: "agent", hidden: false },
    // #5 - Share Tools (HIDDEN until testing done)
    { id: "sharing", label: "Share Tools", icon: Share2, section: "agent", hidden: true },
    // #6 - Referrals
    { id: "referrals", label: "Referrals", icon: UserPlus, section: "agent", hidden: false },
    // #7 - Training
    { id: "training", label: "Training", icon: BookOpen, section: "agent", hidden: false },
    // #8 - Compliance
    { id: "compliance", label: "Compliance", icon: Shield, section: "agent", hidden: false },
    // #9 - Campaign Selection (bottom of left menu)
    { id: "campaign-selection", label: "Campaign Selection", icon: Target, section: "agent", hidden: false },
    
    // Sales Manager specific sections
    { id: "divider-1", label: "", icon: null, section: "divider", hidden: false },
    { id: "own-sales", label: "My Sales", icon: TrendingUp, section: "manager", hidden: false },
    { id: "own-recruits", label: "My Recruits", icon: Users2, section: "manager", hidden: false },
    { id: "team-sales", label: "Team Sales", icon: DollarSign, section: "manager", hidden: false },
    { id: "team-recruits", label: "Team Recruits", icon: Users, section: "manager", hidden: false },
    { id: "recruit-tracking", label: "Recruit Tracking", icon: ClipboardList, section: "manager", hidden: false },
    { id: "downline-tree", label: "Downline Tree", icon: GitBranch, section: "manager", hidden: false },
    { id: "recruit-approval", label: "Recruit Approval", icon: UserCheck, section: "manager", hidden: false },
    { id: "agent-management", label: "Agent Management", icon: ShieldAlert, section: "manager", hidden: false },
    { id: "organization-manager", label: "Team Organization", icon: LayoutGrid, section: "manager", hidden: false },
  ]

  const renderContent = () => {
    switch (activeTab) {
      // Agent functionality
      case "campaign-selection":
        return (
          <AgentCampaignSelector
            campaigns={availableCampaigns.length > 0 ? availableCampaigns : contextCampaigns}
            loading={campaignsLoading}
            selectedCampaignId={selectedCampaign?.id || contextCampaign?.id}
            onCampaignSelect={(campaign) => {
              setSelectedCampaign(campaign)
              contextSelectCampaign(campaign)
              if (intendedDestination) {
                handleTabChange(intendedDestination)
                setIntendedDestination(null)
              }
            }}
          />
        )
      case "enrollment":
        return (
          <TrainingGate moduleType="ENROLLMENT">
            <ClientEnrollmentWizard
              selectedCampaign={selectedCampaign || contextCampaign}
              onCampaignSelect={(campaign) => {
                setSelectedCampaign(campaign)
                contextSelectCampaign(campaign)
              }}
            />
          </TrainingGate>
        )
      case "self-enrollment":
        return (
          <TrainingGate moduleType="SELF_ENROLLMENT">
            <ClientSelfEnrollmentWizard selectedCampaign={selectedCampaign || contextCampaign} />
          </TrainingGate>
        )
      case "sales-reports":
        return (
          <TrainingGate moduleType="SALES_REPORTS">
            <AgentSalesReports />
          </TrainingGate>
        )
      case "training":
        return <TrainingDashboard />
      case "compliance":
        return (
          <TrainingGate moduleType="COMPLIANCE">
            <AgentComplianceMaterials />
          </TrainingGate>
        )
      case "sharing":
        return <AgentShareInstructions />
      case "referrals":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Invite New Recruits</h2>
              <p className="text-gray-600">Share your referral link to grow your team and earn commissions</p>
            </div>
            <ReferralLinkCard />
            <div className="mt-8">
              <OnboardingFunnel />
            </div>
          </div>
        )
      case "pending-applications":
        return <AgentPendingApplications key={pendingViewKey} selectedCampaign={selectedCampaign} />

      // Sales Manager specific sections
      case "own-sales":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">My Sales</h2>
              <p className="text-gray-600">View your personal sales performance</p>
            </div>
            <AgentSalesReports />
          </div>
        )
      case "own-recruits":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">My Recruits</h2>
              <p className="text-gray-600">View recruits you have directly invited</p>
            </div>
            <OnboardingFunnel />
          </div>
        )
      case "team-sales":
        return <TeamSalesReports />
      case "team-recruits":
        return <TeamRecruitsSection />
      case "recruit-tracking":
        return <RecruitTrackingSection />
      case "downline-tree":
        return <DownlineTree />
      case "recruit-approval":
        return <RecruitApprovalPanel />
      case "agent-management":
        return <AgentManagementPanel />
      case "organization-manager":
        return <TeamOrganizationManager />
      default:
        return (
          <div className="card">
            <h2 className="text-xl font-semibold text-accent-900 mb-4">{activeTab.replace("-", " ").toUpperCase()}</h2>
            <div className="bg-accent-50 p-4 rounded-lg">
              <p className="text-sm text-accent-700">
                {activeTab.replace("-", " ")} functionality will be implemented here.
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <Layout title="Sales Manager Dashboard" onMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-0 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      <div className="flex flex-1">
        {/* Sidebar Navigation - Fixed and Responsive */}
        <div
          className={`fixed top-0 sm:top-16 left-0 w-72 h-screen sm:h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-300 p-6 z-10
          ${mobileSidebarOpen ? "block" : "hidden"} lg:block`}
        >
          {/* Mobile header space */}
          <div className="h-14 sm:hidden"></div>

          {/* Campaign Status */}
          {selectedCampaign && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Selected Campaign</h3>
              <div className="p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-800">{selectedCampaign.name}</p>
                    <p className="text-xs text-emerald-600">Campaign Active</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedCampaign(null)
                      contextSelectCampaign(null)
                      handleTabChange('campaign-selection')
                    }}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Agent Section Label */}
          <div className="mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sales Agent</span>
          </div>

          <nav className="space-y-2">
            {navigationItems.filter(item => !item.hidden).map((item) => {
              // Render divider
              if (item.section === "divider") {
                return (
                  <div key={item.id} className="py-3">
                    <div className="border-t border-slate-300"></div>
                    <div className="mt-3">
                      <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Manager Tools</span>
                    </div>
                  </div>
                )
              }

              const Icon = item.icon!
              const isActive = activeTab === item.id
              const managerTools = ['own-sales', 'own-recruits', 'team-sales', 'team-recruits', 'recruit-tracking', 'downline-tree', 'recruit-approval', 'agent-management', 'organization-manager']
              const isDisabled = !['campaign-selection', 'referrals', ...managerTools].includes(item.id) && !selectedCampaign

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isDisabled) {
                      handleTabChange('campaign-selection')
                      setMobileSidebarOpen(false)
                      return
                    }
                    if (item.id === 'enrollment') {
                      if (!resumeRequested) {
                        try { localStorage.removeItem('enrollmentSessionId') } catch { }
                      }
                    }
                    handleTabChange(item.id)
                    if (item.id === 'pending-applications') {
                      setPendingViewKey(Date.now())
                    }
                    setMobileSidebarOpen(false)
                  }}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-200 ${
                    isDisabled
                      ? "bg-slate-50 text-slate-400 cursor-not-allowed opacity-60"
                      : isActive
                        ? "bg-blue-600 text-white shadow-md"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  data-nav-id={item.id}
                >
                  <div className="relative">
                    <Icon className={`w-5 h-5 flex-shrink-0 ${
                      isActive ? "text-white" : isDisabled ? "text-slate-400" : "text-slate-500"
                    }`} />
                  </div>
                  <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.label}
                  </span>
                </button>
              )
            })}
          </nav>
        </div>
        {/* Main Content Area */}
        <div className="flex-1 lg:ml-72 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-14 sm:pt-16">{renderContent()}</div>
      </div>
    </Layout>
  )
}
